import React, { useEffect, useRef, useState } from 'react';

interface DigitalClockProps {
  onActivity: () => void;
}

export const DigitalClock: React.FC<DigitalClockProps> = ({ onActivity }) => {
  const [time, setTime] = useState(new Date());
  const [particles, setParticles] = useState<Array<{
    x: number;
    y: number;
    vx: number;
    vy: number;
    life: number;
    maxLife: number;
  }>>([]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setParticles(prev => {
        const newParticles = prev
          .map(p => ({
            ...p,
            x: p.x + p.vx,
            y: p.y + p.vy,
            life: p.life - 1
          }))
          .filter(p => p.life > 0);

        // Add new particles occasionally
        if (Math.random() < 0.1) {
          for (let i = 0; i < 3; i++) {
            newParticles.push({
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
              vx: (Math.random() - 0.5) * 2,
              vy: (Math.random() - 0.5) * 2,
              life: 60 + Math.random() * 120,
              maxLife: 60 + Math.random() * 120
            });
          }
        }

        return newParticles;
      });
    }, 50);

    return () => clearInterval(interval);
  }, []);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div 
      className="fixed inset-0 cursor-none z-[9999] flex items-center justify-center"
      onClick={onActivity}
      onMouseMove={onActivity}
      onKeyDown={onActivity}
      style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}
    >
      {/* Animated particles */}
      <div className="absolute inset-0 overflow-hidden">
        {particles.map((particle, index) => (
          <div
            key={index}
            className="absolute w-1 h-1 bg-white rounded-full"
            style={{
              left: particle.x,
              top: particle.y,
              opacity: particle.life / particle.maxLife * 0.6,
              transform: `scale(${particle.life / particle.maxLife})`
            }}
          />
        ))}
      </div>

      {/* Main content */}
      <div className="text-center text-white">
        <div className="mb-8">
          <div 
            className="text-8xl font-mono font-bold tracking-wider mb-4"
            style={{
              textShadow: '0 0 30px rgba(255,255,255,0.5), 0 0 60px rgba(255,255,255,0.3)',
              fontFamily: 'monospace'
            }}
          >
            {formatTime(time)}
          </div>
          <div 
            className="text-2xl font-light opacity-90"
            style={{
              textShadow: '0 0 20px rgba(255,255,255,0.3)'
            }}
          >
            {formatDate(time)}
          </div>
        </div>
        
        <div className="text-lg opacity-70 mt-12">
          <div className="mb-2 font-semibold">MapleOS</div>
          <div className="text-sm">Professional Computing Environment</div>
        </div>
      </div>

      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 text-white text-lg opacity-60">
        Time flows elegantly... Move mouse or press any key to exit
      </div>
    </div>
  );
};